/**
 * Which account the widget currently holds a verified session for. Kept on the
 * body next to the widget's visibility classes, for the same reason: the widget
 * is init'd once per page load and outlives the React tree that booted it, so a
 * ref would forget who was chatting and the next login in the same tab would
 * inherit their conversation — the bug verification exists to close
 * (GOTU-5243 / GOTU-5918). Its lifetime matches the widget's: both are torn down
 * only when the document is.
 */
const VERIFIED_FOR_KEY = 'saltclickVerifiedFor';

export const getSaltclickVerifiedForUserId = (): string | undefined =>
  document.body.dataset[VERIFIED_FOR_KEY];

export const setSaltclickVerifiedForUserId = (userId?: string): void => {
  if (userId) {
    document.body.dataset[VERIFIED_FOR_KEY] = userId;

    return;
  }
  delete document.body.dataset[VERIFIED_FOR_KEY];
};

/**
 * `clearSession` is a network round trip to Salesforce, and a hung one must not
 * wedge whatever is waiting on it — logout, an account handover, or the end of a
 * conversation. Past the deadline the clear is treated as failed, which is the
 * safe reading: the marker stays, so the next login retries the handover.
 */
const CLEAR_SESSION_TIMEOUT_MS = 2000;

/**
 * Ends the verified chat session and closes the conversation with it. Resolves
 * `true` when the session is known to be gone.
 *
 * Called on logout, where nothing else takes the conversation down: the widget
 * outlives the React tree, so after logout the tab still holds an open chat
 * verified as the account that just left. Waiting until the next login to clear
 * it is not enough — that only fires for a *different* account, so the same user
 * logging back in would reopen the previous conversation. GOTU-5918.
 *
 * It is NOT what makes the next conversation a new one, despite the name: this ends
 * a messaging *session*, and Salesforce leaves the conversation open and resumable.
 * Retiring a conversation needs `DELETE /iamessage/v1/conversation/{id}`, which no
 * client API on this deployment offers — so what happens to a chat the user ends is
 * Salesforce's business, not the portal's.
 *
 * Safe when there is nothing to end: `userVerificationAPI` exists only on a
 * deployment with User Verification enabled, and never rejects the caller —
 * logout must not fail because Salesforce did.
 */
export const endSaltclickChatSession = async (): Promise<boolean> => {
  const verificationApi = window.embeddedservice_bootstrap?.userVerificationAPI;
  if (!verificationApi) {
    // No widget, or a deployment without User Verification: there is no session
    // for the marker to describe, so it goes either way.
    setSaltclickVerifiedForUserId(undefined);

    return true;
  }

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      // `clearSession` is typed `void | Promise<unknown>`; wrapping makes the
      // synchronous-return deployment resolve immediately rather than hang.
      Promise.resolve(verificationApi.clearSession(true)),
      new Promise((_, reject) => {
        timer = setTimeout(
          () => reject(new Error(`clearSession did not settle in ${CLEAR_SESSION_TIMEOUT_MS}ms`)),
          CLEAR_SESSION_TIMEOUT_MS,
        );
      }),
    ]);
  } catch (err) {
    // The marker is deliberately kept. It is the only record that the widget
    // still holds someone else's verified conversation, so dropping it here would
    // send the next login down the plain verify path — handing a fresh token to a
    // live conversation, which is the carry-over GOTU-5918 exists to close.
    console.error('[Saltclick] Could not end the chat session: ', err);

    return false;
  } finally {
    clearTimeout(timer);
  }

  setSaltclickVerifiedForUserId(undefined);

  return true;
};
